import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import type { HTMLAttributes, MouseEvent, ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { useBreakpoint } from '../../tokens/breakpoints';
import type { Breakpoint } from '../../tokens/breakpoints';
import { Badge } from '../Badge';
import { Checkbox } from '../Checkbox';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { Pagination } from '../Pagination';
import { Spinner } from '../Spinner';
import { Typography } from '../Typography';

import type { SortDirection, TableColumn, TableSort } from './columns';
import styles from './Table.module.css';
import { TableEmptyState } from './TableEmptyState';
import type { TableEmptyStateProps } from './TableEmptyState';
import { useContainerWidth } from './useContainerWidth';

export type { TableColumn, TableAlign, TableSort, SortDirection } from './columns';
export type { TableEmptyStateProps } from './TableEmptyState';

export interface TableLabels {
  selectAll: string;
  selectRow: string;
  search: string;
  sortBy: (header: string) => string;
  /** Visually hidden header of the row-actions column — an empty `<th>` fails axe. */
  actions: string;
}

const DEFAULT_LABELS: TableLabels = {
  selectAll: 'Select all rows',
  selectRow: 'Select row',
  search: 'Search',
  sortBy: (header) => `Sort by ${header}`,
  actions: 'Actions',
};

const DEFAULT_PAGE_SIZE = 10;

/** Extra attributes for one row's element — `<tr>` on desktop, the card's `<li>` on mobile. */
export type TableRowProps = HTMLAttributes<HTMLElement>;
/** Sort affordance in a header cell — 12px, per the Figma table spec (HS-1226). */
const SORT_ICON_SIZE = 12;

/** What a custom card is handed: its selection state, and how to change it. */
export interface TableCardContext {
  selected: boolean;
  toggle: () => void;
  /**
   * `false` when `isRowSelectable` rejects the row: the card should draw no checkbox, and
   * `toggle` does nothing. Always `true` without the predicate.
   */
  selectable: boolean;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  getRowId: (row: T) => string;
  /** Card-shell title. */
  title?: ReactNode;
  /** Count/status badge beside the title. */
  badge?: ReactNode;
  /**
   * A control that belongs to the title rather than to the tools — a view switch, say (HS-961:
   * Reports' History / Schedules). It sits inline after the badge; below the mobile breakpoint it
   * takes a full-width row of its own under the title line. Absent, the header is unchanged.
   */
  titleAddon?: ReactNode;
  /** Primary action (e.g. an Add button) on the right of the header. */
  headerAction?: ReactNode;
  /** Icon-only mobile equivalent of `headerAction`. Falls back to `headerAction`. */
  mobileHeaderAction?: ReactNode;
  /** Extra control left of the search field (e.g. a filter button). */
  filterSlot?: ReactNode;
  /**
   * Message rendered inside the card, between the header and the rows — the position a design
   * uses for a state note that belongs to the table rather than to the page (the Portal's
   * "scoring has not started yet" banner, HS-1440). It survives loading and both empty states,
   * because a notice that disappears whenever the table has nothing to show is a notice that is
   * missing exactly when it explains why.
   */
  notice?: ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Controlled search value. Supplying it delegates filtering to the caller. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  selectable?: boolean;
  /**
   * Controlled selection. Supplying it delegates the selected set to the caller — the table then
   * only reports what the user clicked through `onSelectionChange`, and the caller decides what
   * sticks. Needed wherever something *outside* the table changes the selection: a bulk delete
   * that must clear it, a "select all N across pages" affordance that must set it (HS-1441).
   * Absent, the table keeps the selection itself, as every existing call site expects.
   */
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /**
   * Which rows can be selected at all (HS-1724). A row it rejects draws no checkbox — in the table
   * and on its card — and is left out of the header's "select all" and its all/some state, so a
   * list that mixes kinds of row (the Portal's folders above its files) selects only the kind its
   * bulk actions act on. Absent, every row is selectable, as before.
   */
  isRowSelectable?: (row: T) => boolean;
  /** Trailing per-row actions (e.g. a kebab menu). */
  rowActions?: (row: T) => ReactNode;
  /** Mobile-specific row action. Falls back to `rowActions`. */
  mobileRowActions?: (row: T) => ReactNode;
  /** Place the mobile row action beside the title or as a full-width footer. */
  mobileActionsPlacement?: 'header' | 'footer';
  defaultSort?: TableSort;
  /** Controlled sort. Supplying it delegates sorting to the caller. */
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort) => void;
  /** Rows per page; `0` disables pagination. */
  pageSize?: number;
  /** Controlled current page. Supplying it delegates pagination to the caller. */
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  /** Custom mobile card. Defaults to title + label/value rows built from the columns. */
  renderCard?: (row: T, ctx: TableCardContext) => ReactNode;
  /** Switch to cards at or below this breakpoint. Defaults to `md`. */
  mobileBreakpoint?: Breakpoint;
  /**
   * Also switch to cards — and to the compact header and pager — whenever the table's own box is
   * narrower than this many CSS pixels (HS-961). The viewport rule above still applies; this adds
   * the space the table actually has, which a sidebar or a two-column page takes from the
   * viewport: a nine-column list at 1024 px with the sidebar open gets ~700 px and would otherwise
   * scroll with three columns in view. Measured on the root with a `ResizeObserver`; until the
   * first measurement, on the server and without `ResizeObserver`, the viewport rule alone
   * decides. Pick the width the columns need — their designed widths plus the actions column.
   */
  cardsBelowWidth?: number;
  /**
   * How the cards flow. `list` (the default) stacks one card per row; `grid` places as many
   * columns of cards, each at least 20 rem wide, as the box holds — for a card list that may be
   * shown in a wide box (a tablet, or a desktop page beside a sidebar) where one card stretched
   * across it would push each value far from its label.
   */
  cardLayout?: 'list' | 'grid';
  loading?: boolean;
  /** Shown when there are no rows. */
  emptyState?: TableEmptyStateProps;
  onRowClick?: (row: T) => void;
  /**
   * Extra attributes for a row's own element — the desktop `<tr>` and the mobile card's `<li>`
   * (HS-1776: the Portal's file rows are drag sources and its folder rows drop targets). A generic
   * escape hatch rather than a drag-and-drop API, so the kit carries no product semantics. Its
   * `className` is added to the row's own classes and its `onClick` runs before `onRowClick`;
   * every other attribute is passed as given. Absent, or returning `undefined`, the row is
   * unchanged.
   */
  getRowProps?: (row: T) => TableRowProps | undefined;
  labels?: Partial<TableLabels>;
  /**
   * Desktop column sizing. `auto` (the default) lets each page's content size its columns; `fixed`
   * holds the columns at their `width` on every page, so paging through rows of different lengths
   * does not shift the layout (HS-1440 QA). Give every column a `width` when choosing `fixed` — a
   * column without one shares what is left. A value wider than its column wraps inside it.
   */
  layout?: 'auto' | 'fixed';
  /**
   * `card` (the default) draws the table as its own card. `embedded` drops the card chrome —
   * border, radius and surface — for a table that is one section of a larger card (HS-961: the
   * report detail's data preview); the header, rows and pagination are unchanged.
   */
  variant?: 'card' | 'embedded';
  className?: string;
}

const BREAKPOINT_ORDER: Record<Breakpoint, number> = { sm: 0, md: 1, lg: 2 };

function nextDirection(current: SortDirection | null): SortDirection {
  return current === 'asc' ? 'desc' : 'asc';
}

/**
 * Data table that renders a semantic `<table>` on desktop and a card list on small screens (one
 * card per row). Column-driven: each `TableColumn` supplies a header, a `cell` renderer and an
 * optional `value` for sorting/search. Built-in client-side search, single-column sort (with
 * `aria-sort`), row selection (header select-all + indeterminate), pagination (reusing
 * `Pagination`), and loading/empty states. Reuse existing primitives (`Avatar`, `Badge`, `Button`,
 * links…) inside `cell` for typed content.
 *
 * @interim Seeded from the Figma "Table" (Untitled UI); replaceable by the UI Kit.
 */
export function Table<T>({
  columns,
  data,
  getRowId,
  title,
  badge,
  titleAddon,
  headerAction,
  mobileHeaderAction,
  filterSlot,
  notice,
  searchable = false,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  selectable = false,
  selectedIds: controlledSelectedIds,
  onSelectionChange,
  isRowSelectable,
  rowActions,
  mobileRowActions,
  mobileActionsPlacement = 'header',
  defaultSort,
  sort: controlledSort,
  onSortChange,
  pageSize = DEFAULT_PAGE_SIZE,
  page: controlledPage,
  totalPages: controlledTotalPages,
  onPageChange,
  renderCard,
  mobileBreakpoint = 'md',
  cardsBelowWidth,
  cardLayout = 'list',
  loading = false,
  emptyState,
  onRowClick,
  getRowProps,
  labels,
  layout = 'auto',
  variant = 'card',
  className,
}: TableProps<T>) {
  const messages = useUiMessages();
  const t = { ...DEFAULT_LABELS, ...messages.table, ...labels };
  const loadingLabel = messages.table?.loading ?? 'Loading';
  const breakpoint = useBreakpoint();
  const rootRef = useRef<HTMLElement>(null);
  // The root's width does not depend on which branch renders (it is sized by its parent, and the
  // desktop table scrolls inside it), so switching branches cannot flip the measurement back.
  const containerWidth = useContainerWidth(rootRef, cardsBelowWidth !== undefined);
  const isNarrowBox =
    cardsBelowWidth !== undefined && containerWidth > 0 && containerWidth < cardsBelowWidth;
  const isMobile = BREAKPOINT_ORDER[breakpoint] < BREAKPOINT_ORDER[mobileBreakpoint] || isNarrowBox;

  const [internalQuery, setInternalQuery] = useState('');
  const [internalSort, setInternalSort] = useState<TableSort | null>(defaultSort ?? null);
  const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set());
  const [internalPage, setInternalPage] = useState(1);

  const query = searchValue ?? internalQuery;
  const sort = controlledSort === undefined ? internalSort : controlledSort;
  const page = controlledPage ?? internalPage;
  const isSearchControlled = searchValue !== undefined;
  const isSortControlled = controlledSort !== undefined;
  const isPageControlled = controlledPage !== undefined;
  const isSelectionControlled = controlledSelectedIds !== undefined;
  const selected = useMemo(
    () => (controlledSelectedIds === undefined ? internalSelected : new Set(controlledSelectedIds)),
    [controlledSelectedIds, internalSelected],
  );

  const columnById = useMemo(
    () => new Map(columns.map((column) => [column.id, column])),
    [columns],
  );

  const filtered = useMemo(() => {
    if (isSearchControlled) return data;
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return data;
    return data.filter((row) =>
      columns.some((column) => column.value?.(row).toLowerCase().includes(trimmed) ?? false),
    );
  }, [data, query, columns, isSearchControlled]);

  const sorted = useMemo(() => {
    if (isSortControlled || sort == null) return filtered;
    const column = columnById.get(sort.columnId);
    if (column?.value == null) return filtered;
    const read = column.value;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => read(a).localeCompare(read(b)) * factor);
  }, [filtered, sort, columnById, isSortControlled]);

  const usePaging = pageSize > 0;
  const totalPages =
    controlledTotalPages ?? (usePaging ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1);
  const currentPage = Math.min(page, totalPages);
  const rows =
    usePaging && !isPageControlled
      ? sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : sorted;

  const selectableIds = useMemo(
    () => (isRowSelectable === undefined ? sorted : sorted.filter(isRowSelectable)).map(getRowId),
    [sorted, getRowId, isRowSelectable],
  );
  // Without the predicate the count is the selection's size, exactly as before; with it, only the
  // selectable rows count, so a stray id cannot tip the header box either way.
  const selectedCount =
    isRowSelectable === undefined
      ? selected.size
      : selectableIds.filter((id) => selected.has(id)).length;
  const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const canSelect = (row: T) => selectable && (isRowSelectable?.(row) ?? true);

  const emitSelection = (next: Set<string>) => {
    if (!isSelectionControlled) setInternalSelected(next);
    onSelectionChange?.([...next]);
  };
  const toggleAll = (checked: boolean) => {
    emitSelection(checked ? new Set(selectableIds) : new Set());
  };
  const toggleRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emitSelection(next);
  };

  const onSortClick = (columnId: string) => {
    const next = {
      columnId,
      direction: sort?.columnId === columnId ? nextDirection(sort.direction) : 'asc',
    } satisfies TableSort;
    if (!isSortControlled) setInternalSort(next);
    onSortChange?.(next);
    if (!isPageControlled) setInternalPage(1);
  };

  const ariaSortFor = (columnId: string): 'ascending' | 'descending' | 'none' => {
    if (sort?.columnId !== columnId) return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
  };

  const sortIcon = (columnId: string) => {
    if (sort?.columnId !== columnId) return 'chevron-selector-vertical' as const;
    return sort.direction === 'asc' ? ('arrow-up' as const) : ('arrow-down' as const);
  };

  const showEmpty = !loading && sorted.length === 0 && emptyState != null;

  const titleBlock = (title != null || badge != null) && (
    <div className={styles.headTitle}>
      {title != null && (
        <Typography
          as="h2"
          variant="text-lg"
          weight="semibold"
          className={classNames(styles.title)}
        >
          {title}
        </Typography>
      )}
      {badge != null &&
        (typeof badge === 'string' || typeof badge === 'number' ? (
          <Badge color="brand" size="sm">
            {badge}
          </Badge>
        ) : (
          badge
        ))}
    </div>
  );

  // Inline after the badge on desktop; on mobile it follows the title line (and the action beside
  // it) so the DOM — and therefore the focus order — matches the row it is drawn on.
  const addon = titleAddon != null && <div className={styles.titleAddon}>{titleAddon}</div>;

  const action = isMobile ? (mobileHeaderAction ?? headerAction) : headerAction;
  const header = (title != null ||
    badge != null ||
    titleAddon != null ||
    searchable ||
    filterSlot != null ||
    action != null) && (
    <div
      className={classNames(styles.cardHeader, !isMobile && addon && styles.cardHeaderWithAddon)}
    >
      <div className={styles.headingRow}>
        {titleBlock}
        {!isMobile && addon}
        {isMobile && action != null && <div className={styles.headAction}>{action}</div>}
      </div>
      {isMobile && addon}
      <div className={styles.headTools}>
        {filterSlot != null && <div className={styles.filter}>{filterSlot}</div>}
        {searchable && (
          <div className={styles.search}>
            <Input
              size="sm"
              leadingIcon={<Icon name="search-md" size={18} />}
              placeholder={searchPlaceholder ?? t.search}
              aria-label={t.search}
              value={query}
              onChange={(event) => {
                const next = event.target.value;
                if (!isSearchControlled) setInternalQuery(next);
                onSearchChange?.(next);
                if (!isPageControlled) setInternalPage(1);
              }}
            />
          </div>
        )}
        {!isMobile && action != null && <div className={styles.headAction}>{action}</div>}
      </div>
    </div>
  );

  const pagination = usePaging && !showEmpty && totalPages > 1 && (
    <Pagination
      className={classNames(styles.pagination)}
      // A phone has room for two arrows and "Page X of Y", not for a run of page numbers — the
      // library's mobile pagination, which QA asked for on HS-1440 over text Previous / Next.
      type={isMobile ? 'page-info' : 'numbers'}
      showPreviousNextLabels={!isMobile}
      page={currentPage}
      total={totalPages}
      onPageChange={(nextPage) => {
        if (!isPageControlled) setInternalPage(nextPage);
        onPageChange?.(nextPage);
      }}
    />
  );

  return (
    <section
      ref={rootRef}
      className={classNames(
        styles.root,
        variant === 'embedded' && styles.embedded,
        // The narrow-box branch on a wide viewport, where the mobile media query does not apply.
        isNarrowBox && styles.narrow,
        className,
      )}
    >
      {header}

      {notice != null && <div className={styles.notice}>{notice}</div>}

      {loading ? (
        <div className={styles.loading} role="status" aria-live="polite">
          <Spinner label={loadingLabel} />
        </div>
      ) : showEmpty ? (
        <TableEmptyState {...emptyState} />
      ) : isMobile ? (
        <MobileCards
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          canSelect={canSelect}
          selected={selected}
          toggleRow={toggleRow}
          rowActions={rowActions}
          mobileRowActions={mobileRowActions}
          mobileActionsPlacement={mobileActionsPlacement}
          renderCard={renderCard}
          labels={t}
          onRowClick={onRowClick}
          getRowProps={getRowProps}
          layout={cardLayout}
        />
      ) : (
        <DesktopTable
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          selectable={selectable}
          canSelect={canSelect}
          selected={selected}
          allSelected={allSelected}
          someSelected={someSelected}
          // Nothing on the page to select — every row a folder, say: the header box has no job.
          selectAllDisabled={selectableIds.length === 0}
          toggleAll={toggleAll}
          toggleRow={toggleRow}
          rowActions={rowActions}
          onSortClick={onSortClick}
          ariaSortFor={ariaSortFor}
          sortIcon={sortIcon}
          labels={t}
          onRowClick={onRowClick}
          getRowProps={getRowProps}
          layout={layout}
        />
      )}

      {pagination}
    </section>
  );
}

interface DesktopTableProps<T> {
  rows: T[];
  columns: TableColumn<T>[];
  getRowId: (row: T) => string;
  selectable: boolean;
  canSelect: (row: T) => boolean;
  selected: Set<string>;
  allSelected: boolean;
  someSelected: boolean;
  selectAllDisabled: boolean;
  toggleAll: (checked: boolean) => void;
  toggleRow: (id: string) => void;
  rowActions?: ((row: T) => ReactNode) | undefined;
  onSortClick: (columnId: string) => void;
  ariaSortFor: (columnId: string) => 'ascending' | 'descending' | 'none';
  sortIcon: (columnId: string) => 'chevron-selector-vertical' | 'arrow-up' | 'arrow-down';
  labels: TableLabels;
  onRowClick?: ((row: T) => void) | undefined;
  getRowProps?: ((row: T) => TableRowProps | undefined) | undefined;
  layout: 'auto' | 'fixed';
}

function DesktopTable<T>({
  rows,
  columns,
  getRowId,
  selectable,
  canSelect,
  selected,
  allSelected,
  someSelected,
  selectAllDisabled,
  toggleAll,
  toggleRow,
  rowActions,
  onSortClick,
  ariaSortFor,
  sortIcon,
  labels,
  onRowClick,
  getRowProps,
  layout,
}: DesktopTableProps<T>) {
  return (
    <div className={styles.scroll}>
      <table className={classNames(styles.table, layout === 'fixed' && styles.fixed)}>
        <thead>
          <tr>
            {selectable && (
              <th scope="col" className={styles.selectCell}>
                <Checkbox
                  size="sm"
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={selectAllDisabled}
                  aria-label={labels.selectAll}
                  onChange={toggleAll}
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                aria-sort={column.sortable ? ariaSortFor(column.id) : undefined}
                className={classNames(styles.th, column.align && styles[`align-${column.align}`])}
                style={column.width != null ? { width: column.width } : undefined}
              >
                {column.sortable ? (
                  <button
                    type="button"
                    className={styles.sortButton}
                    aria-label={
                      typeof column.header === 'string' ? labels.sortBy(column.header) : undefined
                    }
                    onClick={() => {
                      onSortClick(column.id);
                    }}
                  >
                    <Typography as="span" variant="text-xs" weight="semibold">
                      {column.header}
                    </Typography>
                    <Icon
                      name={sortIcon(column.id)}
                      size={SORT_ICON_SIZE}
                      className={classNames(styles.sortIcon)}
                    />
                  </button>
                ) : (
                  <Typography as="span" variant="text-xs" weight="semibold">
                    {column.header}
                  </Typography>
                )}
              </th>
            ))}
            {rowActions != null && (
              <th scope="col" className={styles.actionsCell}>
                <span className={styles.srOnly}>{labels.actions}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = getRowId(row);
            const extra = getRowProps?.(row);
            return (
              <tr
                {...extra}
                key={id}
                className={classNames(extra?.className, onRowClick != null && styles.clickable)}
                onClick={composeRowClick(extra?.onClick, onRowClick, row)}
              >
                {selectable && (
                  <td className={styles.selectCell}>
                    {canSelect(row) && (
                      <Checkbox
                        size="sm"
                        checked={selected.has(id)}
                        aria-label={labels.selectRow}
                        onChange={() => {
                          toggleRow(id);
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    )}
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={classNames(column.align && styles[`align-${column.align}`])}
                  >
                    {column.cell(row)}
                  </td>
                ))}
                {rowActions != null && <td className={styles.actionsCell}>{rowActions(row)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface MobileCardsProps<T> {
  rows: T[];
  columns: TableColumn<T>[];
  getRowId: (row: T) => string;
  canSelect: (row: T) => boolean;
  selected: Set<string>;
  toggleRow: (id: string) => void;
  rowActions?: ((row: T) => ReactNode) | undefined;
  mobileRowActions?: ((row: T) => ReactNode) | undefined;
  mobileActionsPlacement: 'header' | 'footer';
  renderCard?: ((row: T, ctx: TableCardContext) => ReactNode) | undefined;
  labels: TableLabels;
  onRowClick?: ((row: T) => void) | undefined;
  getRowProps?: ((row: T) => TableRowProps | undefined) | undefined;
  layout: 'list' | 'grid';
}

function MobileCards<T>({
  rows,
  columns,
  getRowId,
  canSelect,
  selected,
  toggleRow,
  rowActions,
  mobileRowActions,
  mobileActionsPlacement,
  renderCard,
  labels,
  onRowClick,
  getRowProps,
  layout,
}: MobileCardsProps<T>) {
  const primary = columns.find((column) => column.primary) ?? columns[0];
  const rest = columns.filter((column) => column !== primary && column.hideOnMobile !== true);
  const badges = rest.filter((column) => column.mobileVariant === 'badge');
  const details = rest.filter((column) => column.mobileVariant !== 'badge');
  const cardActions = mobileRowActions ?? rowActions;

  return (
    <ul className={classNames(styles.cardList, layout === 'grid' && styles.cardGrid)}>
      {rows.map((row) => {
        const id = getRowId(row);
        const isSelected = selected.has(id);
        const rowSelectable = canSelect(row);
        const toggle = () => {
          if (rowSelectable) toggleRow(id);
        };
        const extra = getRowProps?.(row);
        return (
          <li {...extra} key={id} className={classNames(extra?.className, styles.cardItem)}>
            {renderCard != null ? (
              renderCard(row, { selected: isSelected, toggle, selectable: rowSelectable })
            ) : (
              // Optional whole-card click is a convenience target; the card's own controls
              // (checkbox, links, actions) remain the keyboard-accessible interactions.
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
              <div
                className={classNames(styles.card, onRowClick != null && styles.clickable)}
                onClick={
                  onRowClick != null
                    ? () => {
                        onRowClick(row);
                      }
                    : undefined
                }
              >
                <div className={styles.cardTop}>
                  {rowSelectable && (
                    <Checkbox
                      size="sm"
                      checked={isSelected}
                      aria-label={labels.selectRow}
                      onChange={toggle}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    />
                  )}
                  {primary != null && (
                    <div className={styles.cardTitle}>
                      {(primary.mobileCell ?? primary.cell)(row)}
                    </div>
                  )}
                  {cardActions != null && mobileActionsPlacement === 'header' && (
                    <div className={styles.cardActions}>{cardActions(row)}</div>
                  )}
                </div>
                {badges.length > 0 && (
                  <div className={styles.cardBadges}>
                    {badges.map((column) => (
                      <MobileBadge key={column.id} column={column} row={row} />
                    ))}
                  </div>
                )}
                {details.length > 0 && (
                  <dl className={styles.cardRows}>
                    {details.map((column) => (
                      <div key={column.id} className={styles.cardRow}>
                        <Typography
                          as="dt"
                          variant="text-sm"
                          className={classNames(styles.cardKey)}
                        >
                          {column.header}
                        </Typography>
                        <dd className={styles.cardValue}>
                          {(column.mobileCell ?? column.cell)(row)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
                {cardActions != null && mobileActionsPlacement === 'footer' && (
                  <div className={styles.cardFooter}>{cardActions(row)}</div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The desktop row's click handler: the caller's own `onClick` from `getRowProps` first, then
 * `onRowClick` (HS-1776). Composed rather than overridden, so adding row props never silently
 * drops a row click a page already relies on. `undefined` when neither is given, so a plain row
 * gets no handler at all.
 */
function composeRowClick<T>(
  own: TableRowProps['onClick'],
  onRowClick: ((row: T) => void) | undefined,
  row: T,
): ((event: MouseEvent<HTMLElement>) => void) | undefined {
  if (own == null && onRowClick == null) return undefined;
  return (event) => {
    own?.(event);
    onRowClick?.(row);
  };
}

/**
 * One entry in a mobile card's badge row — the same rule the header's `badge` prop follows: plain
 * text (a string or a number) is drawn as the kit's neutral chip, and so is anything given a
 * `mobileIcon`, since the chip is what carries the icon; any other node is the column's own
 * (usually its own `Badge`) and is rendered as given. Wrapping a cell that already draws a badge
 * put a bordered chip inside a bordered chip. An empty cell draws nothing rather than an empty
 * chip.
 */
function MobileBadge<T>({ column, row }: { column: TableColumn<T>; row: T }) {
  const content = (column.mobileCell ?? column.cell)(row);
  if (content == null || typeof content === 'boolean' || content === '') return null;

  const plain = typeof content === 'string' || typeof content === 'number';
  if (!plain && column.mobileIcon == null) return content;

  return (
    <Badge size="md" variant="modern" color="gray" iconLeading={column.mobileIcon}>
      {content}
    </Badge>
  );
}
